// Shared helpers for the AITS-492 AI Trust Signal cards + detail drawer.
// The signal objects are untyped `any` flattened from report.ts Definition +
// Signal (see ProjectsService.getReportById); these helpers derive the small
// presentational bits the cards/drawer need without inventing data.

/** Three-tier status for a signal, from its score + scoring_criteria. Mirrors
 *  the numeric mapping in `@/variables` (low/medium/high → 1/2/3, fail/pass →
 *  1/3): 3 → pass, 2 → watch, 1 → fail, else unknown. */
export type SignalStatus = "pass" | "watch" | "fail" | "unknown";

export function signalStatus(score?: string | null, criteria?: string[] | null): SignalStatus {
  const s = String(score ?? "").toLowerCase();
  const crit = (criteria ?? []).map((c) => String(c).toLowerCase());
  if (!s || crit.length === 0) return "unknown";

  if (crit.some((c) => ["low", "medium", "high"].includes(c))) {
    if (s === "high") return "pass";
    if (s === "medium") return "watch";
    if (s === "low") return "fail";
    return "unknown";
  }
  if (crit.some((c) => ["fail", "pass"].includes(c))) {
    if (s === "pass") return "pass";
    if (s === "fail") return "fail";
    return "unknown";
  }
  return "unknown";
}

/** Sort rank for a status — most severe first (fail → watch → pass → unknown).
 *  Cards within a tier are ordered by this so the worst signals surface first. */
export function severityRank(status: SignalStatus): number {
  switch (status) {
    case "fail":
      return 0;
    case "watch":
      return 1;
    case "pass":
      return 2;
    default:
      return 3;
  }
}

/** Pill tone for a status (healthy / issue / critical vocabulary). */
export function statusTone(status: SignalStatus): "healthy" | "issue" | "critical" | "neutral" {
  if (status === "pass") return "healthy";
  if (status === "watch") return "issue";
  if (status === "fail") return "critical";
  return "neutral";
}

/** Band class suffix for a 0–100 tier score, on the report's status ramp
 *  (same <70 / <90 thresholds the old tier tab-badge used). */
export function tierBand(pct: number): "lo" | "mid" | "hi" {
  if (pct < 70) return "lo";
  if (pct < 90) return "mid";
  return "hi";
}

/** Map a raw model id (gpt-4o-mini, claude-3.5-sonnet, gemini-2.0-flash, sonar…)
 *  to the provider family key `providerMeta`/`Avatar` expect. */
export function modelFamily(raw?: string | null): string {
  const m = String(raw ?? "").toLowerCase();
  if (m.includes("gpt") || m.includes("openai") || m.startsWith("o1") || m.startsWith("o3")) return "OPENAI";
  if (m.includes("claude") || m.includes("anthropic") || m.includes("sonnet") || m.includes("haiku") || m.includes("opus")) return "CLAUDE";
  if (m.includes("gemini") || m.includes("palm") || m.includes("bard")) return "GEMINI";
  if (m.includes("perplexity") || m.includes("sonar")) return "PERPLEXITY";
  return raw || "Model";
}

/** Strip markdown markers and collapse whitespace to a clean single-line teaser.
 *  The full markdown is rendered elsewhere (drawer sections / response dialog).
 *
 *  Each marker is only stripped where it is actually syntax, because these
 *  summaries are LLM prose about real websites and the same characters show up
 *  literally: "#1 roofing contractor", "revenue > $1M", `snake_case`, "5 * 3".
 *  Blanket-stripping them turned "#1" into "1" on every card. Structural
 *  markers (heading, blockquote, bullet) are therefore anchored to the start of
 *  a line, and emphasis has to hug its content the way markdown requires. */
export function plainExcerpt(text?: string | null, max = 200): string {
  const t = String(text ?? "")
    .replace(/`{1,3}/g, "")
    // Line-anchored structure. Runs before the whitespace collapse below, which
    // is what still lets `^` see the original line breaks.
    .replace(/^[ \t]{0,3}#{1,6}[ \t]+/gm, "")
    .replace(/^[ \t]{0,3}(?:>[ \t]?)+/gm, "")
    .replace(/^[ \t]*[-*•][ \t]+/gm, "")
    // Emphasis: a delimiter pair with no space just inside it. `_` additionally
    // may not be intraword, so identifiers survive. (Matched without lookbehind
    // — Safari < 16.4 fails to parse it, which would take down the bundle.)
    .replace(/(\*\*\*|\*\*|\*)(?=\S)([\s\S]*?\S)\1/g, "$2")
    .replace(/(^|[^A-Za-z0-9_])(___|__|_)(?=\S)([\s\S]*?\S)\2(?![A-Za-z0-9])/g, "$1$3")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
  if (!t) return "";
  return t.length > max ? t.slice(0, max).trimEnd() + "…" : t;
}

/** The prose a signal leads with: the per-report `score_summary` finding when
 *  the run produced one, else the generic definition copy. The card can only
 *  tease this (160 chars, then a 2-line CSS clamp) and the drawer is the only
 *  place to finish reading it, so both surfaces resolve it here — that shared
 *  choice is what guarantees the drawer can always finish what a card started.
 *  `isFinding` tells them apart so each can label them ("What we found" vs
 *  "What this signal measures") and so the card knows to strip markdown. */
export function signalFinding(sig?: any): { text: string; isFinding: boolean } {
  const summary = String(sig?.score_summary ?? "").trim();
  // A summary that strips to nothing is markdown/citation noise, not a usable
  // finding — fall through as if absent, which is what the card did before it
  // shared this helper (`plainExcerpt(...) || explanation`).
  if (summary && plainExcerpt(summary)) return { text: summary, isFinding: true };
  const explanation = String(sig?.definition?.explanation || sig?.explanation || "").trim();
  return { text: explanation, isFinding: false };
}

/** Split a citation URL into a bold domain + a mono path for the source rows.
 *  Falls back to the raw string when it isn't a parseable URL. */
export function displayUrl(url?: string | null): { domain: string; path: string } {
  const raw = String(url ?? "").trim();
  if (!raw) return { domain: "", path: "" };
  try {
    const u = new URL(raw.includes("://") ? raw : `https://${raw}`);
    const path = (u.pathname + u.search).replace(/\/$/, "");
    return { domain: u.hostname.replace(/^www\./, ""), path: path === "" ? "" : path };
  } catch {
    return { domain: raw, path: "" };
  }
}
